import type { Font as DsFont, TpaStyle } from '@wix/document-services-types';
import { defaultStylesMap } from '../constants/textControlsConstants';
import { translate } from '../../translationService';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';

type Font = Omit<DsFont, 'cssColor' | 'fontWithFallbacks'>;

const weights = {
  normal: '400',
  bold: '700',
};

const isBold = (value: number | string | boolean) => {
  if (typeof value === 'boolean') {
    return false;
  }

  const intValue = typeof value === 'string' ? parseInt(value, 10) : value;

  if (Number.isNaN(intValue) && typeof value === 'string') {
    return ['bold', 'bolder'].includes(value);
  }

  return intValue >= 700;
};

export const parseFontString = (fontStr: string): Font => {
  const [style, variant, fontWeight, sizeAndLineHeight, family, color] =
    fontStr.split(' ');
  const [size, lineHeight] = sizeAndLineHeight
    ? sizeAndLineHeight.split('/')
    : [];

  return {
    style: style as Font['style'],
    variant: variant as Font['variant'],
    weight: fontWeight as Font['weight'],
    size,
    lineHeight,
    family: family.replace(/\+/g, ' '),
    color,
    bold: isBold(fontWeight),
    italic: style === 'italic',
  };
};

export const overrideParamsByDisplayName = (
  styleParams: Record<string, any>,
  displayName?: string,
) => {
  if (displayName) {
    styleParams.displayName = displayName;
  } else {
    delete styleParams.displayName;
  }
};

export const overrideCustomPresetParams = (
  fontParam: any,
  key: string,
  value: string | number | boolean,
  experimentsAPI: IExperimentsApiAdapter,
) => {
  const isShowWeightPickerForTPAsTextStylePanelEnabled = experimentsAPI.enabled(
    'se_showWeightPickerForTPAsTextStylePanel',
  );

  switch (key) {
    case 'family':
      fontParam.preset = 'Custom';
      fontParam.family = value;
      break;

    case 'htmlTag':
      fontParam.preset = 'Custom';
      fontParam.htmlTag = value;
      break;

    case 'size':
      fontParam.preset = 'Custom';
      fontParam.size = value;
      break;

    case 'bold':
      fontParam.preset = 'Custom';
      fontParam.style.bold = value;
      if (
        isShowWeightPickerForTPAsTextStylePanelEnabled ||
        fontParam.weight !== undefined
      ) {
        fontParam.weight = value ? weights.bold : weights.normal;
      }
      break;

    case 'weight':
      if (
        isShowWeightPickerForTPAsTextStylePanelEnabled ||
        fontParam.weight !== undefined
      ) {
        fontParam.preset = 'Custom';
        fontParam.weight = value;
        fontParam.style.bold = isBold(value);
      }
      break;

    case 'italic':
      fontParam.preset = 'Custom';
      fontParam.style.italic = value;
      break;

    case 'underline':
      fontParam.preset = 'Custom';
      fontParam.style.underline = value;
      break;
  }
};

export const addFontThemeDisplayNameToTextPresets = async (
  textPresets: TpaStyle['siteTextPresets'],
): Promise<TpaStyle['siteTextPresets']> => {
  const stylesMap: Record<string, any> = {};

  for (const key in defaultStylesMap) {
    const style = defaultStylesMap[key];
    stylesMap[style.cssClass] = style;
  }

  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(textPresets)) {
    const style = stylesMap[value.editorKey];
    if (style) {
      value.displayName = await translate(style.displayName);
      result[key] = value;
    }
  }

  return result;
};
